// Debug logger lato mobile. Scrive in SQLite locale (ring buffer 1000 righe)
// e specchia su console per il dev. Mai throw — il logger non deve rompere
// l'app se la DB locale non è ancora pronta.
//
// `installGlobalErrorHandlers()` aggancia window.onerror + unhandledrejection +
// patch fetch per registrare automaticamente errori e chiamate >2s o non-2xx.

import { appendDebugEvent, listDebugEventsAfter, type DebugLevel } from '../db/local.js';
import { getSupabase, SUPABASE_URL } from '../supabase.js';

// Mirror dei log in localStorage: sopravvive anche se SQLite locale non si
// apre (es. SQLCipher rifiutata, plugin nativo morto). Ring buffer di 200 eventi.
const LS_LOG_KEY = 'niveo.debug_log_v1';
const LS_LOG_MAX = 200;

interface LsLogEntry {
  ts: string;
  area: string;
  level: DebugLevel;
  message: string;
  payload?: unknown;
}

function appendLsLog(entry: LsLogEntry): void {
  try {
    const raw = localStorage.getItem(LS_LOG_KEY);
    const arr: LsLogEntry[] = raw ? (JSON.parse(raw) as LsLogEntry[]) : [];
    arr.push(entry);
    if (arr.length > LS_LOG_MAX) arr.splice(0, arr.length - LS_LOG_MAX);
    localStorage.setItem(LS_LOG_KEY, JSON.stringify(arr));
  } catch { /* noop */ }
}

export function readLsLog(): LsLogEntry[] {
  try {
    const raw = localStorage.getItem(LS_LOG_KEY);
    return raw ? (JSON.parse(raw) as LsLogEntry[]) : [];
  } catch {
    return [];
  }
}

export function clearLsLog(): void {
  try { localStorage.removeItem(LS_LOG_KEY); } catch { /* noop */ }
}

export function logDebug(
  area: string,
  level: DebugLevel,
  message: string,
  payload?: unknown,
): void {
  const consoleFn =
    level === 'error' ? console.error : level === 'warn' ? console.warn : console.info;
  consoleFn(`[${area}]`, message, payload ?? '');
  // Mirror sempre in localStorage: sopravvive a SQLite chiusa.
  appendLsLog({
    ts: new Date().toISOString(),
    area,
    level,
    message,
    payload,
  });
  void appendDebugEvent(area, level, message, payload);
}

export const dbg = {
  info: (area: string, msg: string, payload?: unknown) => logDebug(area, 'info', msg, payload),
  warn: (area: string, msg: string, payload?: unknown) => logDebug(area, 'warn', msg, payload),
  error: (area: string, msg: string, payload?: unknown) => logDebug(area, 'error', msg, payload),
};

/**
 * Riconosce i fallimenti di RETE (device offline / connessione persa). In
 * un'app offline-first questi NON sono errori applicativi: la coda di sync li
 * assorbe e riprova. Loggarli come `error` gonfiava i contatori (~9k
 * error/settimana di puro "Failed to fetch") nascondendo i bug veri. Li
 * declassiamo a `info`.
 */
function isNetworkFailure(msg: string): boolean {
  return /failed to fetch|load failed|networkerror|network request failed|the network connection was lost|connection (?:was )?lost|aborted|abort|timed? ?out|err_(?:internet|network|connection|name_not_resolved)/i.test(
    msg,
  );
}

let installed = false;
let flusherTimer: ReturnType<typeof setInterval> | null = null;
let lastSentId = 0;
let flushing = false;
const FLUSH_INTERVAL_MS = 10_000;
const FLUSH_BATCH = 100;

// --- Gating di ciò che viene SPEDITO al server (debug_traces) ---------------
// Il ring buffer LOCALE (debug_events + localStorage) resta intatto: la
// DiagnosticsPage on-device continua a vedere tutto. Filtriamo solo il flush
// remoto, dove il 97% del volume era telemetria `info` a basso valore
// (`localdb` ~800k/sett, `mobile-sync`/`net` info). Regola:
//   • `warn` + `error`           → SEMPRE (è il segnale che conta).
//   • `info` di aree importanti   → SEMPRE (updater, sync-repair/resume/...).
//   • `info` che "sa di problema" → SEMPRE (fail/error/stuck/reset/recover...).
//   • ogni altro `info`           → campionato (1 ogni 20 = 5%), deterministico
//                                   sull'id (niente Math.random → cache-safe).
const INFO_SAMPLE_EVERY = 20;
const IMPORTANT_INFO_AREAS = new Set([
  'updater',
  'sync-repair',
  'sync-resume',
  'sync-cleanup',
  'sync-online',
  'sync-retry',
  // T-700: snapshot deduplicato (cambio stato o ogni 15 minuti). Deve
  // arrivare sempre al sink: alimenta il monitor senza diventare un heartbeat
  // ad alta frequenza.
  'sync-health',
  // T-724: one bounded snapshot per warehouse visit, only in flagged native pilot builds.
  'native-perf',
  // T-312/#4: ogni write su `events_local` (la PK usata + da quale writer).
  // È telemetria "buona": il volume è proporzionale alle scritture dati reali
  // (azione utente / pull / import), NON alle query come il vecchio firehose
  // `reused_alive`. Sempre flushata così una futura doppia-scrittura (lezione
  // T-311) è visibile nei trace invece di restare campionata via.
  'events_local',
]);
const IMPORTANT_INFO_MSG =
  /fail|error|stuck|reset|timeout|recover|expired|conflict|retry|reject|orphan|mismatch/i;

function shouldFlushToServer(level: DebugLevel, area: string, message: string, id: number): boolean {
  if (level === 'warn' || level === 'error') return true;
  if (IMPORTANT_INFO_AREAS.has(area)) return true;
  if (IMPORTANT_INFO_MSG.test(message)) return true;
  return id % INFO_SAMPLE_EVERY === 0;
}

async function flushDebugEventsToServer(): Promise<void> {
  if (flushing || (typeof navigator !== 'undefined' && !navigator.onLine)) return;
  flushing = true;
  try {
    const events = await listDebugEventsAfter(lastSentId, FLUSH_BATCH);
    if (events.length === 0) return;
    const maxId = events[events.length - 1].id;

    // Tieni solo ciò che vale la pena spedire (vedi shouldFlushToServer).
    const toSend = events.filter((e) => shouldFlushToServer(e.level, e.area, e.message, e.id));
    if (toSend.length === 0) {
      // Batch interamente campionato-via: avanza comunque il cursore così non
      // li ri-leggiamo a ogni tick.
      lastSentId = maxId;
      return;
    }

    const supabase = getSupabase();
    const sess = await supabase.auth.getSession();
    if (supabase !== getSupabase() || (typeof navigator !== 'undefined' && !navigator.onLine)) return;
    const token = sess.data.session?.access_token;
    if (!token) return; // pre-auth: non avanziamo, warn/error restano in coda

    const body = {
      events: toSend.map((e) => ({
        ts: e.ts,
        area: e.area,
        level: e.level,
        message: e.message,
        payload: e.payload,
      })),
    };
    const res = await fetch(`${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/log-ingest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      lastSentId = maxId;
    }
    // Se 4xx/5xx non avanziamo lastSentId — al prossimo tick si riprova.
  } catch {
    // best-effort: niente crash se la rete non c'è
  } finally {
    flushing = false;
  }
}

export function startDebugFlusher(): void {
  if (flusherTimer) return;
  flusherTimer = setInterval(() => { void flushDebugEventsToServer(); }, FLUSH_INTERVAL_MS);
}

export function installGlobalErrorHandlers(): void {
  if (installed) return;
  installed = true;

  window.addEventListener('error', (ev) => {
    logDebug('window.error', 'error', ev.message ?? 'unknown', {
      filename: ev.filename,
      line: ev.lineno,
      col: ev.colno,
    });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev.reason instanceof Error ? ev.reason.message : String(ev.reason);
    // Una promise di rete non gestita (device offline) non è un bug applicativo.
    logDebug('window.unhandledrejection', isNetworkFailure(reason) ? 'info' : 'error', reason);
  });

  // Patch fetch per loggare chiamate lente o fallite.
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    const start = performance.now();
    try {
      const res = await originalFetch(input, init);
      const ms = Math.round(performance.now() - start);
      if (!res.ok) {
        logDebug('net', 'warn', `${res.status} ${url}`, { ms, status: res.status });
      } else if (ms > 2000) {
        logDebug('net', 'info', `slow ${url}`, { ms, status: res.status });
      }
      return res;
    } catch (err) {
      const ms = Math.round(performance.now() - start);
      const errMsg = err instanceof Error ? err.message : String(err);
      // Offline / connessione persa → `info` (atteso in offline-first). Solo i
      // fallimenti NON di rete restano `error`.
      const offline = isNetworkFailure(errMsg);
      logDebug('net', offline ? 'info' : 'error', `fail ${url}`, { ms, error: errMsg, offline });
      throw err;
    }
  };
}
